import { IconButton, Typography } from '@mui/material';
import { SelectedItemsTextContainer } from '@src/components/CommonComponents/Common';
import ConfirmModal from '@src/components/ConfirmModal/ConfirmModal';
import SecurityAlertModal from '@src/components/ConfirmModal/SecurityAlertModal';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomTextButton from '@src/components/CustomButton/CustomTextButton';
import SettingsCard from '@src/components/CustomCard/SettingsCard';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import CustomModal from '@src/components/CustomModal';
import CustomTooltip from '@src/components/CustomTootip/CustomTooltip';
import {
	AcceptIcon,
	AlertCircleIcon,
	DeleteIcon,
	InfoCircleIcon,
	RejectIcon,
} from '@src/components/Icons';
import Loading from '@src/components/Loading/Loading';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import SearchBar from '@src/components/SearchBar';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { CustomAlertType } from '@src/interfaces/type';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	overflow: scroll;
	padding: 24px;
	gap: 24px;
	.container1 {
		display: flex;
		justify-content: space-between;
		align-items: center;
		p {
			color: var(--color-font-grey);
			font-size: 14px;
			font-weight: 600;
		}
	}
	@media screen and (max-width: 600px) {
		margin-bottom: 68px;
	}
`;

const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 750;
	line-height: 21.82px;
	transition: width 0.3s ease;
`;

interface MediaQueries {
	'@media (max-width: 600px)': React.CSSProperties;
}

const stylingDeleteModal: React.CSSProperties & MediaQueries = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '306px',
	height: '375px',
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	'@media (max-width: 600px)': {
		width: '306px',
	},
};

//For Mobile View
const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 55px;
`;

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#order-details {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 16px;
		flex-shrink: 0;
		.field {
			display: flex;
			gap: 6px;
			color: #5c5c5c;
			font-size: 14px;
			font-weight: 500;
			span {
				font-weight: 700;
			}
		}
	}
	#filter-container {
		display: flex;
		gap: 10px;
		padding: 16px;
	}
`;

const Container = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
	marginBottom: '168px',
});

interface Company {
	id: number;
	name: string;
	phone: string;
	address: string;
	image: string | null;
	created_at: string;
	organization_id: number;
	supplier_id: number;
	supplier_phone: string;
	i_gst_type: string;
	i_gstin_number: string;
	i_integration_id: string;
	supplier_create_order: boolean;
}

interface Props {
	displayCustomError: any;
	setAlert: (data: CustomAlertType) => void;
	rows: any;
	setRows: any;
	filteredRows: any;
	setFilteredRows: any;
}

const ApprovalList: React.FC<Props> = ({
	displayCustomError,
	setAlert,
	filteredRows,
	setFilteredRows,
	rows,
	setRows,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);

	const [searchValue, setSearchValue] = useState('');
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

	const [request, setRequest] = useState(false);
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [loadingData, setLoadingData] = useState(true);
	const [rowData, setRowData] = useState<Company | null>(null);
	const [deleteModal, setDeleteModal] = useState(false);

	const isMobileWidth = IsMobileWidth();

	const approveRestaurantRequest = () => {
		setRequest(true);
		apiService.approveRestaurant(
			(res: AxiosResponse) => {
				setAlert({ message: 'Request Approved', severity: 'success' });
				setRequest(false);
				setConfirmDialog(false);

				//next call
				getApprovalList();
			},
			(err: AxiosError) => {
				displayCustomError('Something went wrong!');
				setRequest(false);
			},
			rowData?.id
		);
	};

	const rejectRestaurantRequest = () => {
		setRequest(true);
		apiService.rejectRestaurant(
			(res: AxiosResponse) => {
				setAlert({ message: 'Request Rejected', severity: 'success' });
				setRequest(false);
				setDeleteModal(false);

				//next call
				getApprovalList();
			},
			(err: AxiosError) => {
				displayCustomError('Something went wrong!');
				setRequest(false);
			},
			rowData?.id
		);
	};

	const handleOpenApproveDialog = (row: any) => {
		setRowData(row);
		setConfirmDialog(true);
	};

	const handleOpenRejectDialog = (row: any) => {
		setRowData(row);
		setDeleteModal(true);
	};

	const iconStylyes = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '30px',
		height: '30px',
		borderRadius: '50px',
		background: '#F7F5F4',
		padding: '4px',
	};

	const columns = [
		{ field: 'name', headerName: 'Dealer Name', width: 300 },
		{ field: 'address', headerName: 'Address', width: 400 },
		{ field: 'phone', headerName: 'Virtual ID', width: 200 },
		{
			field: 'actions',
			headerName: 'Actions',
			width: 168,
			renderCell: (params: any) =>
				params?.row?.approved_by_supplier ? (
					<span style={{ color: '#979797', fontSize: '14px', fontWeight: 700 }}>
						Approved in 24 hrs
					</span>
				) : (
					<div id="btns-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
						<CustomTooltip title="Approve" placement="top">
							<div
								style={iconStylyes}
								id="btn-1"
								onClick={() => handleOpenApproveDialog(params?.row)}
							>
								<AcceptIcon id="btn-1-svg" />
							</div>
						</CustomTooltip>
						<CustomTooltip title="Reject" placement="top">
							<div
								style={iconStylyes}
								id="btn-2"
								onClick={() => handleOpenRejectDialog(params?.row)}
							>
								<RejectIcon id="btn-2-svg" />
							</div>
						</CustomTooltip>
					</div>
				),
		},
	];

	const handleSearch = (value: any) => {
		setSearchValue(value);
		const filteredRows: any = rows?.filter((row: any) => {
			const lowerCaseValue = value?.toLowerCase();

			return (
				row?.name?.toLowerCase()?.includes(lowerCaseValue) ||
				row?.id?.toString()?.includes(lowerCaseValue) ||
				row?.address?.toString()?.includes(lowerCaseValue)
			);
		});
		setFilteredRows(filteredRows);
	};

	const getApprovalList = () => {
		apiService.getApprovalRequestList(
			(res: AxiosResponse) => {
				const data = res?.data?.data;
				const updatedData = data?.map((item: any) => {
					return {
						...item,
						acceptDialog: (product: any) => {
							setRowData(product);
							setConfirmDialog(true);
						},
						rejectDialog: (product: any) => {
							setRowData(product);
							setDeleteModal(true);
						},
					};
				});
				setRows(updatedData);
				setFilteredRows(updatedData);
				setLoadingData(false);
			},
			(err: AxiosError) => {
				displayCustomError('Something went wrong!');
				setLoadingData(false);
			}
		);
	};

	const modals = () => {
		return (
			<>
				{' '}
				{confirmDialog ? (
					<CustomModal
						open={confirmDialog}
						setOpen={setConfirmDialog}
						header={false}
						style={{ width: '357px', height: 'auto' }}
						children={
							<SecurityAlertModal
								handleClose={() => setConfirmDialog(false)}
								handleConfirm={() => approveRestaurantRequest()}
								title="Restaurant Approval"
								content={
									<div>
										Would you like to proceed with adding this restaurant to Cheeni's platform, or
										would you prefer to <span style={{ color: '#E6AB00' }}>contact Cheeni</span> for
										further clarification?
									</div>
								}
								btnView="1"
								icon={
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: '48px',
											height: '48px',
											background: '#FFF9E6',
											borderRadius: '50%',
										}}
									>
										<InfoCircleIcon fill="#FFBE00" width="24" height="24" />
									</div>
								}
								loading={request}
							/>
						}
					/>
				) : null}
				{deleteModal ? (
					<CustomModal
						open={deleteModal}
						setOpen={setDeleteModal}
						header={false}
						style={{ width: '357px', height: 'auto' }}
						children={
							<SecurityAlertModal
								handleClose={() => setDeleteModal(false)}
								handleConfirm={() => rejectRestaurantRequest()}
								title="Restaurant Rejection"
								content={
									<div>
										Would you like to proceed with removing this restaurant from Cheeni's platform,
										or would you prefer to <span style={{ color: '#E6AB00' }}>contact Cheeni</span>{' '}
										for further clarification?
									</div>
								}
								btnView="1"
								icon={
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: '48px',
											height: '48px',
											background: '#FFF9E6',
											borderRadius: '50%',
										}}
									>
										<AlertCircleIcon />
									</div>
								}
								loading={request}
							/>
						}
					/>
				) : null}
			</>
		);
	};
	useEffect(() => {
		getApprovalList();
	}, []);

	if (loadingData) {
		return <Loading />;
	}

	if (isMobileWidth) {
		return (
			<StyledMobileContainer>
				{modals()}
				<div id="filter-container">
					<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<SelectedItemsTextContainer
							selectedItemIds={selectedItemIds}
							filteredItems={filteredRows}
							items
						/>
					</div>
				</div>
				<Container>
					{filteredRows?.map((item: any) => {
						return (
							<SettingsCard key={item?.id} item={item} avatarUrl={item?.image} view="approval" />
						);
					})}
				</Container>
			</StyledMobileContainer>
		);
	}

	return (
		<StyledContainer>
			{modals()}
			<div className="container1">
				<SearchBar
					style={{
						width: '328px',
						height: '36px',
					}}
					value={searchValue}
					onChange={handleSearch}
					isLoading={false}
				/>
				{selectedItemIds?.length ? (
					<SelectedItemsContainer>
						<Typography>{selectedItemIds?.length} items selected</Typography>
						<IconButton onClick={() => {}}>
							<DeleteIcon />
						</IconButton>
					</SelectedItemsContainer>
				) : null}
			</div>
			<CustomDataGrid2
				rows={filteredRows}
				columns={columns}
				onRowsSelectionHandler={() => {}}
				handleRowClick={() => {}}
				checkboxSelection={false}
				style={{
					transition: 'height 0.3s ease',
					maxHeight: 'calc(100vh - 220px)',
					minHeight: 'calc(100vh - 220px)',
				}}
			/>
		</StyledContainer>
	);
};

export default ApprovalList;
